/**
 * Provenance of `azureResourceId` in the form (Option C, C3.1; plan §4 C3.1 "Provenance with a durable origin", Codex
 * r2 point 11). A Browse Azure pick writes the ARM id with origin `'picker'` together with the target it was valid for.
 * The rules, all here:
 *
 * - **Only a `'picker'` id is ever cleared automatically**, and only when the target it described changes — a target
 *   field edited — engine, server, port, database, the storage endpoints, emulator and scope (`useAzureProvenance`,
 *   watching the form; a new id written with its target is a replacement pick, not an edit) or a paste / prefill / preset patch landing on the draft
 *   (`withoutStalePickerProvenance`, applied by `applyPatch` for every source but the pick itself).
 * - **Typing in the Data access "Storage account resource ID" field makes the id the user's** (`resourceIdOriginOnEdit`):
 *   origin `'user'`, and no origin once the field is emptied. Editing a picker id by hand turns it `'user'` the same way.
 * - **A missing origin means user-entered** (a record saved before the field, imported, or transferred from an older
 *   version) and is never cleared. A user-entered or unknown-origin id that names another account keeps today's refusal
 *   contract on Connect (`resource-id-mismatch`, `electron/azure-storage/data-access/accountKeyAccess.ts`) — refused,
 *   never replaced.
 * - In edit mode the saved values ARE the target the id was valid for: the watcher records them as it opens.
 */
import { useEffect } from 'react';
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { sameValues } from './engineDrafts';
import type { EnginePatch } from './useEngineDrafts';

/** The fields that name the picked resource: a change to any of them means the id no longer describes the target. */
export const AZURE_TARGET_FIELDS = ['engine', 'server', 'port', 'database', 'azureStorageEndpoints', 'azureStorageEmulator', 'azureStorageScope'] as const;

export type AzureResourceIdOrigin = NonNullable<ConnectionFormValues['azureResourceIdOrigin']>;

export function azureTargetFingerprint(values: Partial<ConnectionFormValues>): string {
  return JSON.stringify(AZURE_TARGET_FIELDS.map((field) => values[field] ?? null));
}

/** What typing in the Data access resource-ID field records: the user's id, or nothing once the field is empty. */
export function resourceIdOriginOnEdit(value: string | undefined): AzureResourceIdOrigin | undefined {
  return value ? 'user' : undefined;
}

/**
 * A patch that did not come from the picker, landing on `base` (the draft it will be applied to): when the draft holds
 * a picker id and the patch touches the target without naming an id of its own, the id and its origin go with it.
 * A user-entered or unknown-origin id is left alone.
 */
export function withoutStalePickerProvenance(patch: EnginePatch, base: Partial<ConnectionFormValues>): EnginePatch {
  if (base.azureResourceIdOrigin !== 'picker' || 'azureResourceId' in patch) return patch;
  const touchesTarget = AZURE_TARGET_FIELDS.some((field) => field !== 'engine' && field in patch && !sameValues(patch[field as keyof EnginePatch], base[field]));
  return touchesTarget ? { ...patch, azureResourceId: undefined, azureResourceIdOrigin: undefined } : patch;
}

type ProvenanceForm = {
  baseStore: { state: { values: ConnectionFormValues }; subscribe: (listener: () => void) => () => void };
  setFieldValue: (field: 'azureResourceId' | 'azureResourceIdOrigin', value: undefined, options: { dontUpdateMeta: boolean }) => void;
};

/**
 * Watches the live form: a picker id whose target changes is cleared, with its origin. The target is recorded when a
 * picker id appears — the pick, a restored draft, an edit-mode open — and again whenever the id itself changes: an
 * authoritative pick writes its id together with its target in one batch, so a NEW id means a replacement (pick A,
 * then B), never an edit of A's target (review C31-01). An engine change is a draft switch or restore, so the new
 * engine's values are recorded, never compared with the previous engine's. Reads the BASE store, as
 * `useModeReconciliation` does (a derived-store listener would see stale values for a write made inside it).
 */
export function useAzureProvenance(form: ProvenanceForm): void {
  useEffect(() => {
    let known: { engine: string; id: string; target: string } | null = null;
    const observe = () => {
      const values = form.baseStore.state.values;
      if (values.azureResourceIdOrigin !== 'picker' || !values.azureResourceId) {
        known = null;
        return;
      }
      const target = azureTargetFingerprint(values);
      if (known === null || known.engine !== values.engine || known.id !== values.azureResourceId) {
        known = { engine: values.engine, id: values.azureResourceId, target };
        return;
      }
      if (known.target === target) return;
      known = null;
      form.setFieldValue('azureResourceId', undefined, { dontUpdateMeta: true });
      form.setFieldValue('azureResourceIdOrigin', undefined, { dontUpdateMeta: true });
    };
    observe();
    return form.baseStore.subscribe(observe);
  }, [form]);
}
